import { useEffect, useState, type ReactNode, type ComponentType, type CSSProperties } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { api, cn, type PlansResponse, type PlanInfo } from '@/lib/utils';
import { PLAN_CATALOG, DEFAULT_TRIAL_DAYS, BRAND } from '@maintenance-tracker/shared';
import { useBillingCopy, usePublicConfig } from '@/lib/public-config';
import {
  Check,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  PhoneCall,
  Phone,
  Send,
  RefreshCw,
  Sparkles,
  Plug,
  LineChart,
  Upload,
  ArrowRight,
  FileText,
  AlertTriangle,
  DollarSign,
  Star,
  Bell,
  Clock,
  Mail,
  MessageSquare,
  Zap,
  Filter,
  Search,
  CalendarClock,
} from 'lucide-react';

const FALLBACK_PLANS: PlanInfo[] = PLAN_CATALOG.map((p) => ({
  id: p.id,
  name: p.name,
  tagline: p.tagline,
  priceMonthly: p.priceMonthly,
  recommended: p.recommended ?? false,
  limits: p.limits,
  features: p.features,
  priceId: null,
}));

const faqs = [
  {
    q: `What is ${BRAND.name}?`,
    a: `${BRAND.name} helps service shops win more maintenance agreement renewals — see who won't renew, who to call first, and automate email, text, and renewal follow-ups so you keep customers instead of losing them quietly.`,
  },
  {
    q: 'Does it replace Jobber, Housecall Pro, or QuickBooks?',
    a: 'No. We sit alongside the tools you already use and plug the renewal gap they do not focus on. Keep your field service software for dispatch and jobs — connect it to us so we can score risk and automate renewals on top.',
  },
  {
    q: 'How is this different from a renewal reminder tool?',
    a: "Reminders are one small piece. We flag customers who probably won't renew, forecast the dollars at risk, rank who to call first, run multi-touch follow-ups, and win back lapsed agreements. It is a renewal system, not a calendar alert.",
  },
  {
    q: 'I track everything in spreadsheets. Is it hard to switch?',
    a: 'No. Paste or upload your customer list and you get a live risk dashboard in minutes — no rip-and-replace, no long onboarding.',
  },
  {
    q: 'Can customers renew online?',
    a: 'You can include a link to a renewal page you already use (your website, invoice portal, or booking page) in reminder emails. We do not process or hold payments for your customers — we are renewal-tracking and communication software.',
  },
  {
    q: 'What happens after the free trial?',
    a: 'Pick a plan that fits the size of your book — from a low-cost entry tier to plans for larger, multi-location shops. Your data stays safe either way.',
  },
];

/** Smaller supporting capabilities shown in the bento grid. */
const gridFeatures = [
  {
    icon: RefreshCw,
    title: 'Renewal links in emails',
    desc: 'Drop a link to your own renewal or invoice page into reminders — customers renew on your existing process while you track status.',
  },
  {
    icon: Sparkles,
    title: 'Equipment replacement leads',
    desc: 'Spot aging equipment — a 12-year-old furnace — and turn a renewal into replacement revenue with one click.',
  },
  {
    icon: Plug,
    title: 'Works with your tools',
    desc: 'Import a spreadsheet today; live QuickBooks, Jobber, and Housecall Pro connections are coming. We never ask you to switch.',
  },
  {
    icon: ShieldCheck,
    title: 'Your data, protected',
    desc: 'Encrypted in transit and at rest, with role-based access. Export or delete your customer list anytime.',
  },
];

const steps = [
  {
    icon: Upload,
    title: 'Import your customers',
    desc: 'Connect your software or upload a spreadsheet. You are live in minutes — no rip-and-replace.',
  },
  {
    icon: ShieldCheck,
    title: 'See who is at risk',
    desc: 'Instantly get a scored list of which customers are about to lapse and how much revenue is on the line.',
  },
  {
    icon: Send,
    title: 'Let it save the revenue',
    desc: 'Automated campaigns, renewal reminders, and win-backs recover the revenue — while you run the business.',
  },
];

const metrics = [
  { value: '15%', label: 'of agreements lapse each year without a renewal system' },
  { value: '$900+', label: 'in repairs & replacement pull-through from one saved customer' },
  { value: '3 min', label: 'from spreadsheet upload to your first at-risk list' },
];

export function LandingPage() {
  return (
    <div className="min-h-screen overflow-x-hidden bg-canvas font-sans text-canvas-fg antialiased">
      <MarketingHeader />
      <Hero />
      <ToolMarquee />
      <ProblemSection />
      <HowItWorksSection />
      <FeatureTabs />
      <FeatureGrid />
      <MetricsBand />
      <PricingSection />
      {/* <BillingNoticeSection /> */}
      <FaqSection />
      <FinalCta />
      <FooterSection />
      <MobileCta />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          }),
        }}
      />
    </div>
  );
}

/* ══════════════════════════ Brand + shared bits ══════════════════════════ */

function Logomark({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'relative inline-flex items-center justify-center overflow-hidden rounded-[11px] shadow-[0_6px_18px_-6px_rgba(43,84,228,0.8)] ring-1 ring-white/15',
        className,
      )}
    >
      <span className="absolute inset-0 bg-gradient-to-br from-accent-bright via-accent to-accent-hover" />
      <span className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent" />
      <svg viewBox="0 0 28 28" fill="none" className="relative h-[58%] w-[58%] text-white">
        <path
          d="M5 19 L11.5 12.5 L15 16 L23 8"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path d="M18.5 8 H23 V12.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="5" cy="19" r="1.5" fill="currentColor" />
      </svg>
    </span>
  );
}

type CtaProps = {
  to?: string;
  href?: string;
  search?: Record<string, string>;
  children: ReactNode;
  variant?: 'primary' | 'outline-dark' | 'outline-light';
  size?: 'md' | 'lg';
  className?: string;
};

export function Cta({ to, href, search, children, variant = 'primary', size = 'lg', className }: CtaProps) {
  const classes = cn(
    'group inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-full font-medium transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2',
    size === 'lg' ? 'h-12 px-6 text-[15px]' : 'h-10 px-4 text-sm',
    variant === 'primary' &&
      'mkt-cta bg-accent text-white shadow-[0_10px_34px_-10px_rgba(43,84,228,0.75)] hover:bg-accent-hover focus-visible:ring-offset-ink',
    variant === 'outline-dark' &&
      'border border-ink-line bg-white/[0.03] text-ink-fg backdrop-blur hover:border-ink-fg/30 hover:bg-white/[0.07] focus-visible:ring-offset-ink',
    variant === 'outline-light' &&
      'border border-canvas-line bg-canvas-raised text-canvas-fg shadow-xs hover:border-canvas-fg/25 focus-visible:ring-offset-canvas',
    className,
  );
  if (to) {
    return (
      <Link to={to} search={search as never} className={classes}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={classes}>
      {children}
    </a>
  );
}

export function Eyebrow({ children, tone = 'light' }: { children: ReactNode; tone?: 'light' | 'dark' }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.16em]',
        tone === 'dark' ? 'text-accent-bright' : 'text-accent',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', tone === 'dark' ? 'bg-accent-bright' : 'bg-accent')} />
      {children}
    </span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  desc,
  align = 'center',
}: {
  eyebrow: string;
  title: ReactNode;
  desc?: string;
  align?: 'center' | 'left';
}) {
  return (
    <div className={cn('max-w-2xl', align === 'center' ? 'mx-auto text-center' : 'text-left')}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-4 font-display text-[28px] font-semibold leading-[1.1] tracking-[-0.025em] text-canvas-fg sm:text-[32px] md:text-[42px]">
        {title}
      </h2>
      {desc && <p className="mt-4 text-[15px] leading-relaxed text-canvas-muted sm:text-[16px]">{desc}</p>}
    </div>
  );
}

/* ══════════════════════════ Header ══════════════════════════ */

export function MarketingHeader({
  signupSearch,
  solid = false,
}: {
  signupSearch?: Record<string, string>;
  solid?: boolean;
} = {}) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (solid) return;
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [solid]);

  const light = solid || scrolled;

  const navLink = cn(
    'hidden px-1 py-1 text-sm transition-colors md:inline-flex',
    light ? 'text-canvas-muted hover:text-canvas-fg' : 'text-ink-muted hover:text-ink-fg',
  );

  return (
    <header className="fixed inset-x-0 top-0 z-40 flex justify-center px-3 pt-3">
      <div
        className={cn(
          'flex h-14 w-full max-w-6xl items-center justify-between gap-2 rounded-2xl px-2.5 transition-all duration-300',
          light
            ? 'border border-canvas-line bg-canvas-raised/85 shadow-[0_8px_30px_-12px_rgba(11,17,32,0.2)] backdrop-blur-xl'
            : 'border border-white/10 bg-white/[0.04] backdrop-blur-md',
        )}
      >
        <Link to="/" className="flex min-w-0 items-center gap-2.5">
          <Logomark className="h-8 w-8 shrink-0" />
          <span className={cn('truncate text-[15px] font-semibold tracking-tight', light ? 'text-canvas-fg' : 'text-ink-fg')}>
            {BRAND.name}
          </span>
        </Link>
        <nav className="flex items-center gap-1 md:gap-6">
          <a href="#how" className={navLink}>
            How it works
          </a>
          <a href="#features" className={navLink}>
            Features
          </a>
          <a href="#pricing" className={navLink}>
            Pricing
          </a>
          <Link to="/blog" className={navLink}>
            Blog
          </Link>
          <span className={cn('mx-1 hidden h-5 w-px md:block', light ? 'bg-canvas-line' : 'bg-white/15')} />
          <Link
            to="/login"
            className={cn(
              'hidden text-sm font-medium transition-colors sm:inline-flex',
              light ? 'text-canvas-fg hover:text-accent' : 'text-ink-fg hover:text-white',
            )}
          >
            Sign in
          </Link>
          <Cta to="/signup" search={signupSearch} size="md" className="shrink-0">
            Start free
          </Cta>
        </nav>
      </div>
    </header>
  );
}

/* ══════════════════════════ Hero ══════════════════════════ */

function Hero() {
  return (
    <section className="mkt-grain relative overflow-hidden bg-ink pt-28 pb-16 md:pt-36 md:pb-24">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(62% 55% at 50% -10%, rgba(91,132,255,0.28), transparent 62%), radial-gradient(40% 45% at 88% 8%, rgba(123,92,255,0.16), transparent 60%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse 78% 55% at 50% 0%, black, transparent 76%)',
        }}
      />

      <div className="relative mx-auto max-w-4xl px-4 text-center">
        <h1 className="mkt-reveal font-display text-[38px] font-semibold leading-[1.03] tracking-[-0.03em] text-ink-fg sm:text-[52px] md:text-[70px]">
          Win more renewals.
          <br />
          <span className="bg-gradient-to-r from-accent-bright via-[#8aa5ff] to-accent-2 bg-clip-text text-transparent">
            Keep more revenue.
          </span>
        </h1>

        <p
          className="mkt-reveal mx-auto mt-6 max-w-xl text-[16px] leading-relaxed text-ink-muted sm:text-[18px]"
          style={{ animationDelay: '0.06s' }}
        >
          {BRAND.description}
        </p>

        <div
          className="mkt-reveal mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row"
          style={{ animationDelay: '0.12s' }}
        >
          <Cta to="/signup" size="lg" className="w-full sm:w-auto">
            Start free trial
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Cta>
          <Cta href="#how" variant="outline-dark" size="lg" className="w-full sm:w-auto">
            See how it works
          </Cta>
        </div>

        <div
          className="mkt-reveal mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[13px] text-ink-muted"
          style={{ animationDelay: '0.18s' }}
        >
          <span className="inline-flex items-center gap-1.5">
            <span className="flex">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" />
              ))}
            </span>
            Loved by shop owners
          </span>
          <span className="hidden h-3 w-px bg-ink-line sm:block" />
          <span>No credit card required</span>
          <span className="hidden h-3 w-px bg-ink-line sm:block" />
          <span>Live in minutes</span>
        </div>
      </div>

      <div className="mkt-reveal relative mx-auto mt-16 max-w-5xl px-4" style={{ animationDelay: '0.24s' }}>
        <div
          className="mkt-glow pointer-events-none absolute -inset-x-16 -top-12 bottom-0"
          style={{ background: 'radial-gradient(50% 60% at 50% 0%, rgba(91,132,255,0.32), transparent 70%)' }}
        />
        <div className="relative">
          <FloatChip
            className="absolute -left-3 top-24 z-20 hidden animate-[mkt-float_6s_ease-in-out_infinite] lg:flex"
            icon={DollarSign}
            tone="accent"
            title="+$4,200 recovered"
            sub="Winter tune-up renewals"
          />
          <FloatChip
            className="absolute -right-4 top-10 z-20 hidden animate-[mkt-float_7s_ease-in-out_infinite] lg:flex"
            style={{ animationDelay: '1s' }}
            icon={Bell}
            tone="indigo"
            title="Reminder sent"
            sub="60-day renewal sequence"
          />
          <MockDashboard />
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Mock window primitives ══════════════════════════ */

export function MockChrome({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-black/5 bg-white shadow-[0_40px_120px_-40px_rgba(11,17,32,0.55)] ring-1 ring-black/5',
        className,
      )}
    >
      <div className="flex items-center gap-2 border-b border-canvas-line bg-[#f8f9fc] px-4 py-2.5">
        <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
        <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
        <span className="h-3 w-3 rounded-full bg-[#28C840]" />
        <div className="ml-3 flex min-w-0 items-center gap-2 rounded-md bg-white px-2.5 py-1 text-[11px] text-canvas-muted shadow-xs ring-1 ring-canvas-line">
          <span className="h-2 w-2 shrink-0 rounded-full bg-accent" />
          <span className="truncate">{title}</span>
        </div>
      </div>
      {children}
    </div>
  );
}

export function FloatChip({
  icon: Icon,
  title,
  sub,
  tone,
  className,
  style,
}: {
  icon: ComponentType<{ className?: string }>;
  title: string;
  sub: string;
  tone: 'accent' | 'indigo';
  className?: string;
  style?: CSSProperties;
}) {
  return (
    <div
      style={style}
      className={cn(
        'items-center gap-2.5 rounded-xl border border-black/5 bg-white/95 px-3 py-2.5 shadow-[0_18px_40px_-16px_rgba(11,17,32,0.5)] backdrop-blur',
        className,
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 items-center justify-center rounded-lg',
          tone === 'accent' ? 'bg-accent-soft text-accent' : 'bg-[#efeaff] text-accent-2',
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div>
        <p className="text-[12px] font-semibold leading-tight text-canvas-fg">{title}</p>
        <p className="text-[11px] leading-tight text-canvas-muted">{sub}</p>
      </div>
    </div>
  );
}

export const RISK_PILL: Record<string, string> = {
  High: 'bg-[#FEE2E2] text-[#991B1B]',
  Medium: 'bg-[#FEF3C7] text-[#92400E]',
  Low: 'bg-[#DCFCE7] text-[#166534]',
};

export function Avatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('');
  return (
    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-soft text-[10px] font-semibold text-accent">
      {initials}
    </span>
  );
}

/* ── Hero dashboard ── */
type DashStat = { label: string; value: string; icon: ComponentType<{ className?: string }>; tone: string };
type DashRow = { name: string; reason: string; risk: string };

const DEFAULT_DASH_STATS: DashStat[] = [
  { label: 'Revenue at risk', value: '$27,400', icon: DollarSign, tone: 'text-danger' },
  { label: 'Likely to lapse', value: '18%', icon: TrendingDown, tone: 'text-warning' },
  { label: 'High risk', value: '12', icon: AlertTriangle, tone: 'text-danger' },
  { label: 'Active plans', value: '612', icon: FileText, tone: 'text-accent' },
];

const DEFAULT_DASH_ROWS: DashRow[] = [
  { name: 'Michael Residence', reason: 'Renewal 8 days overdue · unpaid invoice', risk: 'High' },
  { name: 'Tampa Bay Offices', reason: 'No service in 13 months', risk: 'High' },
  { name: 'Riverside Cafe', reason: 'Renews in 20 days · not opening emails', risk: 'Medium' },
  { name: 'Hillcrest Dental', reason: 'Furnace is 12 yrs old · upsell ready', risk: 'Medium' },
];

export function MockDashboard({
  title = "Mike's Comfort HVAC — Renewal dashboard",
  expected = '$142,900',
  stats = DEFAULT_DASH_STATS,
  rows = DEFAULT_DASH_ROWS,
}: {
  title?: string;
  expected?: string;
  stats?: DashStat[];
  rows?: DashRow[];
} = {}) {
  return (
    <MockChrome title={title}>
      <div className="flex">
        <div className="hidden w-14 shrink-0 flex-col items-center gap-1 border-r border-canvas-line bg-[#fbfcfe] py-4 sm:flex">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent text-white">
            <TrendingUp className="h-4 w-4" />
          </span>
          {[LineChart, PhoneCall, Send, ShieldCheck].map((Icon, i) => (
            <span key={i} className="flex h-8 w-8 items-center justify-center rounded-lg text-canvas-muted hover:bg-canvas">
              <Icon className="h-4 w-4" />
            </span>
          ))}
        </div>
        <div className="min-w-0 flex-1 bg-white p-4 md:p-5">
          <div className="flex items-end justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-wider text-canvas-muted">This quarter</p>
              <p className="mt-1 font-display text-xl font-semibold tracking-tight text-canvas-fg sm:text-2xl md:text-3xl">
                {expected} <span className="text-sm font-normal text-canvas-muted">expected renewals</span>
              </p>
            </div>
            <span className="hidden shrink-0 items-center gap-1 rounded-full bg-accent-soft px-2.5 py-1 text-[12px] font-semibold text-accent sm:inline-flex">
              <TrendingUp className="h-3.5 w-3.5" /> +11.4%
            </span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2.5 md:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="rounded-xl border border-canvas-line bg-[#fbfcfe] p-3">
                <s.icon className={cn('h-4 w-4', s.tone)} />
                <p className="mt-2 text-lg font-semibold leading-none text-canvas-fg">{s.value}</p>
                <p className="mt-1.5 text-[11px] leading-tight text-canvas-muted">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between px-0.5">
            <p className="text-[12px] font-semibold text-canvas-fg">Call this week</p>
            <span className="text-[11px] text-canvas-muted">Sorted by risk &amp; value</span>
          </div>
          <div className="mt-2 overflow-hidden rounded-xl border border-canvas-line">
            {rows.map((r, i) => (
              <div key={r.name} className={cn('flex items-center gap-3 bg-white px-3 py-2.5', i > 0 && 'border-t border-canvas-line')}>
                <Avatar name={r.name} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-canvas-fg">{r.name}</p>
                  <p className="truncate text-[11px] text-canvas-muted">{r.reason}</p>
                </div>
                <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold', RISK_PILL[r.risk])}>{r.risk}</span>
                <span className="hidden shrink-0 items-center gap-1 rounded-full border border-canvas-line px-2 py-1 text-[10px] font-medium text-canvas-fg sm:inline-flex">
                  <Send className="h-3 w-3 text-accent" /> Follow up
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </MockChrome>
  );
}

/* ── Risk table ── */
export function MockRiskTable() {
  const rows = [
    { name: 'Northgate Apartments', plan: 'Commercial', renews: 'Aug 2', score: 91, risk: 'High', reason: 'Unpaid invoice · 14mo no service' },
    { name: 'Michael Residence', plan: 'Residential', renews: 'Jul 26', score: 88, risk: 'High', reason: 'Renewal overdue 8 days' },
    { name: 'Riverside Cafe', plan: 'Commercial', renews: 'Aug 9', score: 64, risk: 'Medium', reason: 'Not opening emails' },
    { name: 'Delgado Family', plan: 'Residential', renews: 'Sep 1', score: 41, risk: 'Medium', reason: 'Skipped fall tune-up' },
    { name: 'Summit Offices', plan: 'Commercial', renews: 'Sep 18', score: 12, risk: 'Low', reason: 'On track · auto-pay on' },
  ];
  return (
    <MockChrome title="Renewal risk · All customers">
      <div className="bg-white p-3 md:p-4">
        <div className="flex items-center justify-between gap-2 pb-3">
          <div className="flex min-w-0 items-center gap-2 rounded-lg border border-canvas-line bg-[#fbfcfe] px-2.5 py-1.5 text-[12px] text-canvas-muted">
            <Search className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Search customers</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-lg border border-canvas-line px-2.5 py-1.5 text-[12px] text-canvas-fg">
              <Filter className="h-3.5 w-3.5 text-accent" /> Risk
            </span>
            <span className="hidden rounded-lg bg-accent px-2.5 py-1.5 text-[12px] font-medium text-white sm:inline">Export</span>
          </div>
        </div>
        <div className="overflow-hidden rounded-xl border border-canvas-line">
          <div className="grid grid-cols-[1.6fr_0.9fr_1.4fr] items-center gap-2 border-b border-canvas-line bg-[#f8f9fc] px-3 py-2 text-[10.5px] font-semibold uppercase tracking-wide text-canvas-muted md:grid-cols-[1.6fr_0.8fr_0.7fr_1fr_1.4fr]">
            <span>Customer</span>
            <span className="hidden md:block">Plan</span>
            <span className="hidden md:block">Renews</span>
            <span>Risk</span>
            <span className="hidden md:block">Why</span>
          </div>
          {rows.map((r, i) => (
            <div
              key={r.name}
              className={cn(
                'grid grid-cols-[1.6fr_0.9fr_1.4fr] items-center gap-2 px-3 py-2.5 md:grid-cols-[1.6fr_0.8fr_0.7fr_1fr_1.4fr]',
                i > 0 && 'border-t border-canvas-line',
              )}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <Avatar name={r.name} />
                <span className="truncate text-[13px] font-medium text-canvas-fg">{r.name}</span>
              </div>
              <span className="hidden text-[12px] text-canvas-muted md:block">{r.plan}</span>
              <span className="hidden text-[12px] text-canvas-muted md:block">{r.renews}</span>
              <div className="flex items-center gap-2">
                <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold', RISK_PILL[r.risk])}>{r.risk}</span>
                <span className="hidden text-[11px] font-semibold text-canvas-fg sm:inline">{r.score}</span>
              </div>
              <span className="hidden truncate text-[12px] text-canvas-muted md:block">{r.reason}</span>
            </div>
          ))}
        </div>
      </div>
    </MockChrome>
  );
}

/* ── Call-this-week worklist ── */
export function MockWorklist() {
  const rows = [
    { name: 'Northgate Apartments', phone: '(813) 555-0148', reason: 'Unpaid invoice · 14mo no service', value: '$3,600' },
    { name: 'Michael Residence', phone: '(727) 555-0111', reason: 'Renewal overdue 8 days', value: '$540' },
    { name: 'Riverside Cafe', phone: '(813) 555-0193', reason: 'Not opening emails', value: '$1,200' },
  ];
  return (
    <MockChrome title="Worklist · Call this week">
      <div className="bg-white p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-wider text-canvas-muted">Priority queue</p>
            <p className="mt-0.5 text-[15px] font-semibold text-canvas-fg">7 calls · $9,480 at risk</p>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-soft px-2.5 py-1 text-[12px] font-semibold text-accent">
            <CalendarClock className="h-3.5 w-3.5" /> Today
          </span>
        </div>
        <div className="mt-3 space-y-2">
          {rows.map((r, i) => (
            <div key={r.name} className="flex items-center gap-3 rounded-xl border border-canvas-line bg-[#fbfcfe] px-3 py-2.5">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-ink-fg">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-medium text-canvas-fg">{r.name}</p>
                <p className="truncate text-[11px] text-canvas-muted">
                  <Phone className="mr-1 inline h-3 w-3" />
                  {r.phone} · {r.reason}
                </p>
              </div>
              <span className="hidden text-[12px] font-semibold text-danger sm:inline">{r.value}</span>
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[11px] font-medium text-white">
                <Phone className="h-3 w-3" /> Call
              </span>
            </div>
          ))}
        </div>
      </div>
    </MockChrome>
  );
}

/* ── Automated campaign builder + email preview ── */
export function MockCampaign({ trade = 'HVAC' }: { trade?: string } = {}) {
  const nodes = [
    { icon: Zap, label: '60 days before renewal', tag: 'Trigger', tone: 'accent' },
    { icon: Mail, label: 'Send renewal reminder', tag: 'Email', tone: 'plain' },
    { icon: Clock, label: 'Wait 5 days', tag: 'Delay', tone: 'plain' },
    { icon: MessageSquare, label: 'Text if not renewed', tag: 'SMS', tone: 'plain' },
    { icon: DollarSign, label: 'Win-back offer', tag: 'Email', tone: 'gold' },
  ];
  return (
    <MockChrome title="Automations · Renewal sequence">
      <div className="grid gap-3 bg-white p-4 sm:grid-cols-[1.1fr_1fr]">
        <div className="relative">
          <div className="absolute left-[19px] top-4 bottom-4 w-px bg-canvas-line" />
          <div className="space-y-2.5">
            {nodes.map((n) => (
              <div key={n.label} className="relative flex items-center gap-3">
                <span
                  className={cn(
                    'z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border',
                    n.tone === 'accent' && 'border-accent/30 bg-accent text-white',
                    n.tone === 'gold' && 'border-[#f2d79a] bg-[#fdf6e6] text-[#b7791f]',
                    n.tone === 'plain' && 'border-canvas-line bg-[#fbfcfe] text-accent',
                  )}
                >
                  <n.icon className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1 rounded-lg border border-canvas-line bg-white px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-canvas-muted">{n.tag}</p>
                  <p className="truncate text-[12.5px] font-medium text-canvas-fg">{n.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-xl border border-canvas-line bg-[#fbfcfe] p-3">
          <div className="flex items-center justify-between border-b border-canvas-line pb-2">
            <span className="text-[11px] font-semibold text-canvas-fg">Preview</span>
            <Mail className="h-3.5 w-3.5 text-canvas-muted" />
          </div>
          <p className="mt-2 truncate text-[11px] text-canvas-muted">To: michael@residence.com</p>
          <p className="text-[13px] font-semibold text-canvas-fg">Your {trade} plan renews soon</p>
          <div className="mt-2 space-y-1.5">
            <span className="block h-2 w-full rounded bg-canvas-line" />
            <span className="block h-2 w-[85%] rounded bg-canvas-line" />
            <span className="block h-2 w-[70%] rounded bg-canvas-line" />
          </div>
          <span className="mt-3 inline-flex items-center gap-1 rounded-lg bg-accent px-2.5 py-1.5 text-[11px] font-medium text-white">
            Renew my plan <ArrowRight className="h-3 w-3" />
          </span>
          <p className="mt-3 inline-flex items-center gap-1 text-[10.5px] text-accent">
            <Check className="h-3 w-3" /> Delivered · opened 2× · clicked
          </p>
        </div>
      </div>
    </MockChrome>
  );
}

/* ── Revenue-at-risk forecast chart ── */
export function MockForecast() {
  const bars = [
    { m: 'Apr', total: 46, risk: 10 },
    { m: 'May', total: 58, risk: 14 },
    { m: 'Jun', total: 52, risk: 12 },
    { m: 'Jul', total: 70, risk: 22 },
    { m: 'Aug', total: 64, risk: 16 },
    { m: 'Sep', total: 82, risk: 20 },
    { m: 'Oct', total: 74, risk: 12 },
    { m: 'Nov', total: 90, risk: 15 },
  ];
  const max = 100;
  return (
    <MockChrome title="Forecast · Revenue at risk">
      <div className="bg-white p-4 md:p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex gap-6">
            <div>
              <p className="text-[11px] text-canvas-muted">Expected renewals</p>
              <p className="font-display text-xl font-semibold tracking-tight text-canvas-fg sm:text-2xl">$142,900</p>
            </div>
            <div>
              <p className="text-[11px] text-canvas-muted">At risk</p>
              <p className="font-display text-xl font-semibold tracking-tight text-danger sm:text-2xl">$27,400</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-canvas-muted">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-accent" /> Expected
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-sm bg-danger" /> At risk
            </span>
          </div>
        </div>
        <div className="mt-5 flex h-40 items-end gap-2 sm:gap-3">
          {bars.map((b) => (
            <div key={b.m} className="flex flex-1 flex-col items-center gap-1.5">
              <div className="flex w-full flex-col justify-end" style={{ height: '128px' }}>
                <div className="w-full rounded-t-md bg-danger/80" style={{ height: `${(b.risk / max) * 128}px` }} />
                <div className="w-full bg-accent" style={{ height: `${((b.total - b.risk) / max) * 128}px` }} />
              </div>
              <span className="text-[10px] text-canvas-muted">{b.m}</span>
            </div>
          ))}
        </div>
      </div>
    </MockChrome>
  );
}

/* ══════════════════════════ Tool marquee ══════════════════════════ */

export function ToolMarquee({ tools: toolsProp }: { tools?: string[] } = {}) {
  const { data: publicConfig } = usePublicConfig();
  const defaultTools = publicConfig?.integrationsLive
    ? ['QuickBooks', 'Jobber', 'Housecall Pro', 'ServiceTitan', 'Spreadsheets', 'CSV import']
    : ['Spreadsheets', 'QuickBooks', 'Jobber', 'Housecall Pro', 'ServiceTitan', 'CSV import'];
  const tools = toolsProp && toolsProp.length > 0 ? toolsProp : defaultTools;
  const loop = [...tools, ...tools];
  return (
    <section className="border-b border-canvas-line bg-canvas py-10">
      <p className="px-4 text-center text-[13px] font-medium text-canvas-muted">
        Works alongside the tools you already use — we never ask you to switch
      </p>
      <div className="mkt-marquee-mask mt-6 overflow-hidden">
        <div className="mkt-marquee-track gap-12 px-6">
          {loop.map((t, i) => (
            <span key={i} className="whitespace-nowrap text-[17px] font-semibold text-canvas-fg/40">
              {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Problem ══════════════════════════ */

type ProblemItem = { icon?: ComponentType<{ className?: string }>; title: string; desc: string };

const DEFAULT_PROBLEM_ICONS = [TrendingDown, PhoneCall, AlertTriangle];

const DEFAULT_PROBLEMS: ProblemItem[] = [
  {
    icon: TrendingDown,
    title: 'Quiet drop-offs',
    desc: "Customers stop opening emails, skip a tune-up, leave an invoice unpaid — then quietly let the agreement lapse. By the time you notice, they're gone.",
  },
  {
    icon: PhoneCall,
    title: 'Manual chasing',
    desc: 'Someone in the office calls down a spreadsheet, guessing who to contact. Most never get called. Renewals slip through.',
  },
  {
    icon: AlertTriangle,
    title: 'Zero visibility',
    desc: "Owners can't answer 'how much renewal revenue is at risk this quarter?' until it's already lost. FSM software optimizes jobs, not renewals.",
  },
];

export function ProblemSection({
  eyebrow = 'The leak',
  title = (
    <>
      Your next $50k isn&apos;t in new leads —
      <br className="hidden md:block" /> it&apos;s leaking out the back
    </>
  ),
  desc = 'Most shops pour money into getting customers, then quietly lose them at renewal. Three things make it bleed:',
  problems = DEFAULT_PROBLEMS,
}: {
  eyebrow?: string;
  title?: ReactNode;
  desc?: string;
  problems?: ProblemItem[];
} = {}) {
  return (
    <section className="relative px-4 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <SectionHeading eyebrow={eyebrow} title={title} desc={desc} />
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {problems.map(({ icon, title: cardTitle, desc: cardDesc }, i) => {
            const Icon = icon ?? DEFAULT_PROBLEM_ICONS[i % DEFAULT_PROBLEM_ICONS.length];
            return (
              <div
                key={cardTitle}
                className="rounded-2xl border border-canvas-line bg-canvas-raised p-6 transition-all duration-200 hover:border-canvas-fg/15 hover:shadow-[0_16px_40px_-20px_rgba(11,17,32,0.2)]"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-danger-subtle text-danger">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-5 text-[17px] font-semibold text-canvas-fg">{cardTitle}</h3>
                <p className="mt-2 text-[14px] leading-relaxed text-canvas-muted">{cardDesc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ How it works ══════════════════════════ */

export function HowItWorksSection({ signupSearch }: { signupSearch?: Record<string, string> } = {}) {
  return (
    <section id="how" className="relative overflow-hidden border-y border-canvas-line bg-canvas-raised px-4 py-20 md:py-28">
      <div className="mkt-dotgrid pointer-events-none absolute inset-0 opacity-60" />
      <div className="relative mx-auto max-w-6xl">
        <SectionHeading eyebrow="How it works" title="From customer list to recovered revenue in 3 steps" />
        <div className="relative mt-16 grid gap-10 md:grid-cols-3 md:gap-8">
          <div className="absolute left-[16%] right-[16%] top-7 hidden h-px bg-gradient-to-r from-transparent via-canvas-line to-transparent md:block" />
          {steps.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="relative text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-canvas-line bg-canvas text-accent shadow-sm">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="mt-5 text-[18px] font-semibold text-canvas-fg">{title}</h3>
              <p className="mx-auto mt-2 max-w-xs text-[14px] leading-relaxed text-canvas-muted">{desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 flex justify-center">
          <Cta to="/signup" search={signupSearch} variant="outline-light">
            Try it free
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Cta>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Feature tabs (Salient-style showcase) ══════════════════════════ */

export type FeatureTab = {
  tab: string;
  icon: ComponentType<{ className?: string }>;
  title: string;
  desc: string;
  Mock: ComponentType;
};

export const featureTabs: FeatureTab[] = [
  {
    tab: 'Risk dashboard',
    icon: ShieldCheck,
    title: 'See who won\u2019t renew',
    desc: 'Every agreement is scored Low, Medium, or High risk with a plain-language reason — so nothing slips away quietly.',
    Mock: MockRiskTable,
  },
  {
    tab: 'Call worklist',
    icon: PhoneCall,
    title: 'Know exactly who to call first',
    desc: 'A ranked queue of your highest-value, highest-risk customers — with their number and the reason — so your office stops dialing blind.',
    Mock: MockWorklist,
  },
  {
    tab: 'Automations',
    icon: Send,
    title: 'Automate the follow-up',
    desc: 'Multi-touch email and text sequences run on their own from 60 days out through win-back — instead of someone calling 300 people by hand.',
    Mock: MockCampaign,
  },
  {
    tab: 'Forecast',
    icon: LineChart,
    title: 'Know your revenue at risk',
    desc: 'Expected renewal revenue, dollars at risk, and share likely to lapse — the numbers owners care about, updated daily.',
    Mock: MockForecast,
  },
];

export function FeatureTabs({
  tabs = featureTabs,
  eyebrow = 'The system',
  title = 'A complete renewal system, not another reminder app',
  desc = 'Spot at-risk renewals, prioritize your day, and automate the follow-up — all in one place.',
}: {
  tabs?: FeatureTab[];
  eyebrow?: string;
  title?: string;
  desc?: string;
} = {}) {
  const [active, setActive] = useState(0);
  const current = tabs[active] ?? tabs[0];
  const ActiveMock = current.Mock;

  return (
    <section id="features" className="px-4 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <SectionHeading eyebrow={eyebrow} title={title} desc={desc} />

        <div className="mt-14 grid gap-8 lg:grid-cols-[22rem_1fr] lg:gap-12">
          {/* Tabs */}
          <div className="-mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 scroll-slim lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0">
            {tabs.map((t, i) => {
              const isActive = i === active;
              return (
                <button
                  key={t.tab}
                  type="button"
                  onClick={() => setActive(i)}
                  className={cn(
                    'w-[78%] shrink-0 snap-start rounded-2xl border p-5 text-left transition-all duration-200 sm:w-[58%] lg:w-full lg:shrink',
                    isActive
                      ? 'border-accent/40 bg-canvas-raised shadow-[0_16px_40px_-24px_rgba(43,84,228,0.4)]'
                      : 'border-transparent hover:bg-canvas-raised/70',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={cn(
                        'flex h-9 w-9 shrink-0 items-center justify-center rounded-xl transition-colors',
                        isActive ? 'bg-accent text-white' : 'bg-accent-soft text-accent',
                      )}
                    >
                      <t.icon className="h-4 w-4" />
                    </span>
                    <h3 className={cn('font-display text-[16px] font-semibold', isActive ? 'text-canvas-fg' : 'text-canvas-muted')}>
                      {t.title}
                    </h3>
                  </div>
                  <p className={cn('mt-3 text-[13.5px] leading-relaxed', isActive ? 'text-canvas-muted' : 'text-canvas-muted/70')}>
                    {t.desc}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Panel */}
          <div className="relative min-w-0">
            <div
              className="pointer-events-none absolute -inset-6 -z-10 rounded-[32px]"
              style={{ background: 'radial-gradient(60% 60% at 50% 40%, rgba(43,84,228,0.1), transparent 70%)' }}
            />
            <div key={active} className="mkt-reveal">
              <ActiveMock />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Feature grid (bento) ══════════════════════════ */

type GridFeature = { icon: ComponentType<{ className?: string }>; title: string; desc: string };

export function FeatureGrid({
  eyebrow = 'More in the box',
  title = 'Built for the way service shops actually run',
  features = gridFeatures,
}: {
  eyebrow?: string;
  title?: string;
  features?: GridFeature[];
} = {}) {
  return (
    <section className="border-t border-canvas-line bg-canvas-raised px-4 py-20 md:py-24">
      <div className="mx-auto max-w-6xl">
        <SectionHeading eyebrow={eyebrow} title={title} />
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {features.map(({ icon: Icon, title: cardTitle, desc }) => (
            <div
              key={cardTitle}
              className="group rounded-2xl border border-canvas-line bg-canvas p-6 transition-all duration-200 hover:border-accent/40 hover:shadow-[0_16px_40px_-22px_rgba(43,84,228,0.28)]"
            >
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-soft text-accent transition-colors group-hover:bg-accent group-hover:text-white">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-[15.5px] font-semibold text-canvas-fg">{cardTitle}</h3>
              <p className="mt-1.5 text-[13.5px] leading-relaxed text-canvas-muted">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Metrics band ══════════════════════════ */

type Metric = { value: string; label: string };

const DEFAULT_METRICS_BODY = (
  <>
    A single maintenance customer is worth far more than the agreement — count the repairs and the eventual
    replacement and one save is often <span className="font-semibold text-ink-fg">$900+</span> in pull-through. A
    300-agreement shop losing 15% a year is quietly bleeding{' '}
    <span className="font-semibold text-ink-fg">tens of thousands</span>.
  </>
);

export function MetricsBand({
  eyebrow = 'The math',
  title = 'Recover one customer and it pays for itself for a year',
  body = DEFAULT_METRICS_BODY,
  metrics: metricsProp = metrics,
  ctaLabel = 'See your revenue at risk free',
  signupSearch,
}: {
  eyebrow?: string;
  title?: ReactNode;
  body?: ReactNode;
  metrics?: Metric[];
  ctaLabel?: string;
  signupSearch?: Record<string, string>;
} = {}) {
  return (
    <section className="px-4 py-6">
      <div className="mkt-grain relative mx-auto max-w-6xl overflow-hidden rounded-[28px] bg-ink px-6 py-14 md:px-14 md:py-16">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(50% 80% at 12% 0%, rgba(91,132,255,0.24), transparent 60%), radial-gradient(45% 70% at 100% 100%, rgba(123,92,255,0.18), transparent 60%)',
          }}
        />
        <div className="relative grid items-center gap-10 lg:grid-cols-[1.1fr_1fr]">
          <div>
            <Eyebrow tone="dark">{eyebrow}</Eyebrow>
            <h2 className="mt-4 font-display text-[26px] font-semibold leading-[1.12] tracking-[-0.02em] text-ink-fg sm:text-[32px] md:text-[38px]">
              {title}
            </h2>
            <p className="mt-4 max-w-lg text-[15px] leading-relaxed text-ink-muted sm:text-[15.5px]">{body}</p>
            <div className="mt-8">
              <Cta to="/signup" search={signupSearch} size="lg">
                {ctaLabel}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </Cta>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {metricsProp.map((m) => (
              <div key={m.label} className="rounded-2xl border border-ink-line bg-white/[0.03] p-5 backdrop-blur">
                <p className="font-display text-4xl font-semibold tracking-tight text-accent-bright">{m.value}</p>
                <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Pricing ══════════════════════════ */

export function PricingSection({ signupSearch }: { signupSearch?: Record<string, string> } = {}) {
  const { data } = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<PlansResponse>('/api/v1/plans'),
  });
  const plans = data?.plans ?? FALLBACK_PLANS;
  const trialDays = data?.trialDays ?? DEFAULT_TRIAL_DAYS;

  return (
    <section id="pricing" className="px-4 py-20 md:py-28">
      <div className="mx-auto max-w-6xl">
        <SectionHeading
          eyebrow="Pricing"
          title="Pricing that pays for itself"
          desc={`${trialDays}-day full-feature trial · No credit card required · Cancel anytime. Priced on the revenue you keep, not per technician.`}
        />
        <div className="mt-14 grid items-stretch gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => {
            const featured = plan.recommended;
            return (
              <div
                key={plan.id}
                className={cn(
                  'relative flex flex-col rounded-2xl p-6 transition-all duration-200',
                  featured
                    ? 'mkt-grain bg-ink text-ink-fg shadow-[0_30px_70px_-30px_rgba(43,84,228,0.65)] lg:-my-2 lg:py-8'
                    : 'border border-canvas-line bg-canvas-raised hover:border-canvas-fg/20 hover:shadow-[0_16px_40px_-24px_rgba(11,17,32,0.2)]',
                )}
              >
                {featured && (
                  <div
                    className="pointer-events-none absolute inset-0 rounded-2xl"
                    style={{ background: 'radial-gradient(80% 50% at 50% 0%, rgba(91,132,255,0.22), transparent 60%)' }}
                  />
                )}
                <div className="relative flex flex-1 flex-col">
                  <div className="flex items-center justify-between">
                    <h3 className={cn('text-[17px] font-semibold', featured ? 'text-white' : 'text-canvas-fg')}>{plan.name}</h3>
                    {featured && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-[10.5px] font-semibold text-white">
                        <Star className="h-3 w-3 fill-white" /> Popular
                      </span>
                    )}
                  </div>
                  <p className={cn('mt-1 min-h-[2.5rem] text-[13px]', featured ? 'text-ink-muted' : 'text-canvas-muted')}>
                    {plan.tagline}
                  </p>
                  <p className="mt-3 flex items-baseline gap-1">
                    <span
                      className={cn('font-display text-[36px] font-semibold tracking-tight', featured ? 'text-white' : 'text-canvas-fg')}
                    >
                      ${plan.priceMonthly}
                    </span>
                    <span className={featured ? 'text-ink-muted' : 'text-canvas-muted'}>/mo</span>
                  </p>
                  <ul className="mt-5 flex-1 space-y-2.5">
                    {plan.features.map((l) => (
                      <li key={l} className={cn('flex items-start gap-2.5 text-[13px]', featured ? 'text-ink-fg' : 'text-canvas-fg')}>
                        <span
                          className={cn(
                            'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full',
                            featured ? 'bg-accent/25' : 'bg-accent-soft',
                          )}
                        >
                          <Check className={cn('h-2.5 w-2.5', featured ? 'text-accent-bright' : 'text-accent')} strokeWidth={3} />
                        </span>
                        {l}
                      </li>
                    ))}
                  </ul>
                  <Cta
                    to="/signup"
                    search={signupSearch}
                    variant={featured ? 'primary' : 'outline-light'}
                    size="md"
                    className="mt-6 w-full"
                  >
                    Start free trial
                  </Cta>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function BillingNoticeSection() {
  const billing = useBillingCopy();
  return (
    <section className="border-t border-canvas-line bg-canvas px-4 py-10">
      <div className="mx-auto max-w-3xl text-center text-[13px] leading-relaxed text-canvas-muted">
        <p className="font-medium text-canvas-fg">{BRAND.name} is business management software (SaaS).</p>
        <p className="mt-2">{billing.landingBillingLine}</p>
      </div>
    </section>
  );
}

/* ══════════════════════════ FAQ (Salient-style grid) ══════════════════════════ */

export function FaqSection({ faqs: faqsProp = faqs }: { faqs?: { q: string; a: string }[] } = {}) {
  const per = Math.ceil(faqsProp.length / 3);
  const columns = [faqsProp.slice(0, per), faqsProp.slice(per, per * 2), faqsProp.slice(per * 2)];
  return (
    <section className="relative overflow-hidden border-t border-canvas-line bg-canvas px-4 py-20 md:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(45% 40% at 12% 8%, rgba(43,84,228,0.06), transparent 60%), radial-gradient(40% 40% at 92% 95%, rgba(123,92,255,0.06), transparent 60%)',
        }}
      />
      <div className="relative mx-auto max-w-6xl">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-[28px] font-semibold leading-[1.1] tracking-[-0.025em] text-canvas-fg sm:text-[32px] md:text-[40px]">
            Frequently asked questions
          </h2>
          <p className="mt-4 text-[15px] leading-relaxed text-canvas-muted sm:text-[16px]">
            Can&apos;t find what you&apos;re looking for?{' '}
            <a href={`mailto:${BRAND.supportEmail}`} className="font-medium text-accent hover:text-accent-hover">
              Reach out to the CEO
            </a>{' '}
            and we&apos;ll help.
          </p>
        </div>
        <ul className="mx-auto mt-14 grid max-w-2xl grid-cols-1 gap-8 sm:mt-16 sm:max-w-none sm:grid-cols-2 lg:grid-cols-3">
          {columns.map((col, ci) => (
            <li key={ci}>
              <ul className="flex flex-col gap-8">
                {col.map((f) => (
                  <li key={f.q} className="rounded-2xl border border-canvas-line bg-canvas-raised p-6">
                    <h3 className="font-display text-[15.5px] font-semibold leading-snug text-canvas-fg">{f.q}</h3>
                    <p className="mt-3 text-[14px] leading-relaxed text-canvas-muted">{f.a}</p>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ══════════════════════════ Final CTA ══════════════════════════ */

export function FinalCta({
  title = 'Find the revenue hiding in your customer list',
  desc = 'Import your customers and see exactly who is about to lapse — in minutes, free.',
  signupSearch,
}: {
  title?: string;
  desc?: string;
  signupSearch?: Record<string, string>;
} = {}) {
  return (
    <section className="px-4 py-16 md:py-20">
      <div className="mkt-grain relative mx-auto max-w-5xl overflow-hidden rounded-[32px] bg-ink px-6 py-16 text-center md:py-20">
        <div
          className="mkt-glow pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(55% 70% at 50% 0%, rgba(91,132,255,0.3), transparent 65%)' }}
        />
        <div className="relative">
          <Eyebrow tone="dark">Get started</Eyebrow>
          <h2 className="mx-auto mt-4 max-w-2xl font-display text-[28px] font-semibold leading-[1.08] tracking-[-0.02em] text-ink-fg sm:text-[36px] md:text-[46px]">
            {title}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-[15px] leading-relaxed text-ink-muted sm:text-[16px]">{desc}</p>
          <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Cta to="/signup" search={signupSearch} size="lg" className="w-full sm:w-auto">
              Start free trial
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Cta>
            <Cta to="/login" variant="outline-dark" size="lg" className="w-full sm:w-auto">
              Sign in
            </Cta>
          </div>
          <p className="mt-5 inline-flex items-center gap-2 text-[13px] text-ink-muted">
            <Clock className="h-3.5 w-3.5" /> No credit card required · Cancel anytime
          </p>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════ Footer ══════════════════════════ */

const LEGAL_LINKS: [string, string][] = [
  ['Privacy', '/privacy'],
  ['Terms', '/terms'],
  ['Refunds', '/refunds'],
  ['Security', '/security'],
  ['Cookies', '/cookies'],
  ['DPA', '/dpa'],
];

export function FooterSection() {
  const trades: [string, string][] = [
    ['HVAC', 'hvac'],
    ['Plumbing', 'plumbing'],
    ['Electrical', 'electrical'],
    ['Pest control', 'pest-control'],
    ['Lawn care', 'lawn-care'],
    ['Pool & spa', 'pool-spa'],
  ];
  return (
    <footer className="border-t border-canvas-line bg-canvas">
      <div className="mx-auto max-w-6xl px-4 py-14">
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1.3fr]">
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <Logomark className="h-8 w-8" />
              <span className="text-[15px] font-semibold tracking-tight text-canvas-fg">{BRAND.name}</span>
            </Link>
            <p className="mt-4 max-w-xs text-[13.5px] leading-relaxed text-canvas-muted">
              The renewal system for service shops — see who won&apos;t renew, who to call first, and automate the
              follow-up so revenue stops walking out the back.
            </p>
            <div className="mt-5 flex items-center gap-1 text-[13px] text-canvas-muted">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-gold text-gold" />
              ))}
              <span className="ml-1.5">Loved by shop owners</span>
            </div>
          </div>

          <FooterCol title="Product">
            <FooterA href="#features">Features</FooterA>
            <FooterA href="#how">How it works</FooterA>
            <FooterA href="#pricing">Pricing</FooterA>
            <FooterL to="/blog">Blog</FooterL>
          </FooterCol>

          <FooterCol title="By trade">
            {trades.map(([label, slug]) => (
              <Link
                key={slug}
                to="/for/$niche"
                params={{ niche: slug }}
                className="text-[13.5px] text-canvas-muted transition-colors hover:text-accent"
              >
                {label}
              </Link>
            ))}
          </FooterCol>

          <FooterCol title="Get started">
            <FooterL to="/signup">Start free trial</FooterL>
            <FooterL to="/login">Sign in</FooterL>
            <a
              href={`mailto:${BRAND.supportEmail}`}
              className="mt-1 inline-flex w-fit items-center gap-1.5 rounded-xl border border-accent/30 bg-accent-soft px-3.5 py-2.5 text-[13.5px] font-semibold text-accent transition-colors hover:border-accent/50 hover:bg-accent-soft/70"
            >
              Reach out to the CEO
              <ArrowRight className="h-3.5 w-3.5" />
            </a>
          </FooterCol>
        </div>
      </div>

      <div className="border-t border-canvas-line">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 py-6 text-[13px] text-canvas-muted sm:flex-row sm:justify-between">
          <p>{BRAND.copyright}</p>
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            {LEGAL_LINKS.map(([label, to]) => (
              <Link key={to} to={to} className="transition-colors hover:text-canvas-fg">
                {label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12px] font-semibold uppercase tracking-[0.12em] text-canvas-fg">{title}</p>
      {children}
    </div>
  );
}

function FooterA({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} className="text-[13.5px] text-canvas-muted transition-colors hover:text-accent">
      {children}
    </a>
  );
}

function FooterL({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link to={to} className="text-[13.5px] text-canvas-muted transition-colors hover:text-accent">
      {children}
    </Link>
  );
}

export function MobileCta({ signupSearch }: { signupSearch?: Record<string, string> } = {}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-canvas-line bg-canvas/95 p-3 backdrop-blur md:hidden">
      <Cta to="/signup" search={signupSearch} className="w-full">
        Start free trial
      </Cta>
    </div>
  );
}
